import { hasIntegrationAtom, integrationDataAtom } from '@/atoms/integrationAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { isMobileHeaderSheetOpenAtom } from '@/components/layout/header/mobile-app-header/mobile-header-sheet-atoms';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { useMutateCreateNewSessionForOtherUser } from '@/hooks/mutation/useMutateCreateNewSessionForOtherUser';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { useNewSessionMutationPending } from '@/hooks/useNewSession';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import {
  beginSessionCreateAttempt,
  markSessionCreateMode,
  type SessionCreateAttemptOwner,
} from '@/lib/telemetry/sessionCreateTracking';
import { type RootState } from '@/store';
import { Button } from '@heidi/design-system/button';
import { externalLaunchContextAtom } from '@heidi/integrations/ehr/data/atoms';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtomValue, useSetAtom } from 'jotai';
import { PlusCircle } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * New Session action button for mobile panel header.
 * Extracted from SessionBarNewSessionMobileButton for use in MobileSidebar header.
 * Hidden when launched from any EHR integration (Epic FHIR or generic EHR like Healthdirect).
 */
export function SessionsPanelAction() {
  const isFhirAuth = useIsFhirAuth();
  const isGenericEhrLaunch = Boolean(
    useAtomValue(externalLaunchContextAtom)?.ehrProvider
  );
  const isEhrLaunch = isFhirAuth || isGenericEhrLaunch;

  const { t } = useTranslations();
  const isMobile = useIsMobile();
  const { mutate: onNewSessionClick } = useMutateCreateNewSession();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const { mutate: onNewSessionForOtherUserClick } =
    useMutateCreateNewSessionForOtherUser();
  const { checkRBACPermission } = useRBACPermissionCheck();
  const hasIntegration = useAtomValue(hasIntegrationAtom);
  const integrationData = useAtomValue(integrationDataAtom);
  const isRunning = useSelector((rootState: RootState) => rootState.timerModel.isRunning);
  const setIsMobileHeaderSheetOpen = useSetAtom(isMobileHeaderSheetOpenAtom);

  const isCreatingSession = useNewSessionMutationPending();

  const handleNewSessionClick = useCallback(() => {
    // A tap while any create is in flight must not open a second attempt.
    const beginAttempt = (): SessionCreateAttemptOwner | undefined => {
      if (isCreatingSession) return undefined;
      beginSessionCreateAttempt({ entryPoint: 'mobile_sessions_panel' });
      // Online only: this button skips useNewSession's offline route.
      markSessionCreateMode('online');
      return {};
    };
    if (!isReviewingOtherUser) {
      const attempt = beginAttempt();
      onNewSessionClick(attempt ? { attempt } : undefined);
    } else if (teamDetails?.team.id && selectedReviewUser) {
      onNewSessionForOtherUserClick({
        teamId: teamDetails?.team.id,
        memberId: selectedReviewUser,
        attempt: beginAttempt(),
      });
    }
    setIsMobileHeaderSheetOpen(false);
  }, [
    isCreatingSession,
    isReviewingOtherUser,
    onNewSessionClick,
    onNewSessionForOtherUserClick,
    selectedReviewUser,
    teamDetails?.team.id,
    setIsMobileHeaderSheetOpen,
  ]);

  const shouldEnableNewSessionButton = useMemo(() => {
    return checkRBACPermission(
      PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_CREATE,
      PERMISSION_SUBJECTS.SESSION_CREATE
    );
  }, [checkRBACPermission]);

  const isDisabled = useMemo(() => {
    return (
      !shouldEnableNewSessionButton ||
      isRunning ||
      (hasIntegration &&
        (!integrationData.patientId || integrationData.patientId === '""'))
    );
  }, [
    shouldEnableNewSessionButton,
    isRunning,
    hasIntegration,
    integrationData.patientId,
  ]);

  if (isEhrLaunch) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      size={isMobile ? 'md' : 'sm'}
      onClick={handleNewSessionClick}
      disabled={isDisabled}
    >
      <PlusCircle />
      {t('sidenav.newSession')}
    </Button>
  );
}
